import { useRef, useState } from 'react';
import './FileUpload.css';

/**
 * Themed file upload with click + drag-and-drop, file chips and remove buttons.
 * Controlled: pass `files` (a File for single, or File[] for multiple) and `onChange`.
 */
export default function FileUpload({
  label,
  hint,
  accept = 'application/pdf,image/*',
  multiple = false,
  files,
  onChange,
  required = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const list = multiple ? (files || []) : (files ? [files] : []);

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    if (multiple) onChange([...(files || []), ...arr]);
    else onChange(arr[0] || null);
  };

  const removeAt = (idx) => {
    if (multiple) onChange((files || []).filter((_, i) => i !== idx));
    else onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div className="file-upload">
      <span className="file-upload-label">
        {label} {required && <span className="req-star">*</span>}
        {hint && <span className="file-upload-hint">{hint}</span>}
      </span>

      <div
        className={`file-dropzone ${dragOver ? 'dragover' : ''} ${list.length ? 'has-files' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
      >
        <svg className="file-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <span className="file-dropzone-text"><strong>Klik om te uploaden</strong> of sleep hierheen</span>
        <span className="file-dropzone-sub">PDF of afbeelding{multiple ? ' — meerdere mogelijk' : ''}</span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        onChange={(e) => addFiles(e.target.files)}
      />

      {list.length > 0 && (
        <ul className="file-list">
          {list.map((f, i) => (
            <li key={i} className="file-item">
              <svg className="file-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <span className="file-item-name">{f.name}</span>
              <span className="file-item-size">{fmtSize(f.size)}</span>
              <button
                type="button"
                className="file-item-remove"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                aria-label={`Verwijder ${f.name}`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
